import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { ColorSAP, FONT_FAMILY, Typography } from '../../ontego-ui';

import { AppContext } from '../../App';
import { Description, FooterContainer, LinkRegister, LoginContainer, PlayButton, RootContainer, Title, VideoContainer, VideoThumbnail, VideoThumbnailContainer } from './LoginStyles';

import Grid from '@mui/material/Grid2';


import { useMediaQuery, useTheme } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ROLE_ADMIN } from '../../Constants/ClientRoles';
import { ADMIN_DASHBOARD, REGISTER_PATH, SCENARIO_PATH } from '../../Constants/Path';
import VideoDialog from '../../Dialogs/VideoDialog/VideoDialog';
import Play from '../../Icons/Play';
import Button from '../../ReusableComponents/Button/Button';
import Form from '../../ReusableComponents/Form/Form';
import { LanguageSwitcher } from '../../ReusableComponents/LanguageSwitcher/LanguageSwitcher';
import { timeout } from '../../Utils/Utils';
import { LogoDark } from '../../assets/img/logo/logoDark';
import OntegoCockpitThumbnail from '../../assets/videos/thumbnail-ontego-cockpit.webm';

const Login = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [emailError, setEmailError] = useState(false)
    const [emailInvalid, setEmailInvalid] = useState(false);
    const [passwordError, setPasswordError] = useState(false)
    const [errorMessage, setErrorMessage] = useState("");

    const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);

    const { t, i18n } = useTranslation();
    
    const theme = useTheme();
    const isMobileDisplay = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobileTabletDisplay = useMediaQuery(theme.breakpoints.down('md'));

    const {
        setIsLoginLoading,
        AuthService,
        setUserInfo,
        handleShowErrorSnackbar
    } = useContext(AppContext);

    const onChangeLanguageClick = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem("lang", lang)
    };

    const handleChangeEmail = e => {
        setEmail(e.currentTarget.value)
        setEmailError(false)
        setEmailInvalid(false)
        setErrorMessage("")
    };

    const handleChangePassword = e => {
        setPassword(e.currentTarget.value)
        setPasswordError(false)
        setErrorMessage("")
    };

    const handleUserKeyPress = useCallback(event => {
        const { keyCode } = event;

        if (keyCode === 13) {
            if (document.getElementById('loginButton')) document.getElementById('loginButton').click()
        }
    }, []);

    const handlePlayVideo = () => {
        setIsVideoDialogOpen(true)
    }

    const handleCloseVideo = () => {
        setIsVideoDialogOpen(false)
    }

    useEffect(() => {
        window.addEventListener('keydown', handleUserKeyPress);

        return () => {
            window.removeEventListener('keydown', handleUserKeyPress);
        };
    }, [handleUserKeyPress]);

    useEffect(() => {
        const userAgent = window.navigator.userAgent;
    }, [])

    const login = async e => {
        e.preventDefault();
        setIsLoginLoading(true)

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email) || email === "" || password === "") {
            setEmailError(!email)
            setEmailInvalid(!emailRegex.test(email))
            setPasswordError(!password)
            setIsLoginLoading(false)
            return;
        }

        const credentials = { email: email, password: password, name: "" };
        AuthService.login(credentials).then(res => {
            if (res.data.login) {
                localStorage.setItem("userInfo", JSON.stringify(res.data.token));

                AuthService.setUserToken(res.data.token);

                const decoded = jwtDecode(res.data.token);

                // Set current user
                setUserInfo(decoded);
                setIsLoginLoading(false)

                if (decoded.role === ROLE_ADMIN) {
                    navigate(ADMIN_DASHBOARD)
                } else {
                    navigate(SCENARIO_PATH)
                }
            } else {
                if (res.data.msg) {
                    console.log(res.data.msg)
                    setErrorMessage(res.data.msg);
                } else {
                    setErrorMessage(t("error.emailPasswordWrong"));
                }

                setEmail("")
                setPassword("")
                setIsLoginLoading(false)
            }
        }, error => {
            console.log(error)
            setEmail("")
            setPassword("")
            setErrorMessage(t("error.internalErrorLogin"));
            setIsLoginLoading(false)
        }).catch(err => {
            console.log(err);
            handleShowErrorSnackbar(err);
        });
        await timeout(2000)
        setIsLoginLoading(false)
    }

    return (
        <RootContainer>
            <Grid container spacing={5} justifyContent="space-between">
                <Grid item size={12}>
                    <Grid container direction="row-reverse" style={{ height: isMobileTabletDisplay ? "auto" : "100%" }}>
                        <Grid item size={{ xs: 12, sm: 12, md: 6 }} style={{position: 'relative'}}>
                            {!isMobileDisplay && 
                                <>
                                    <VideoThumbnailContainer>
                                        <VideoThumbnail
                                            playsInline
                                            autoPlay
                                            muted
                                            loop
                                            preload="none"
                                        >
                                            <source
                                                src={OntegoCockpitThumbnail}
                                                codecs="vp8, vorbis"
                                            />
                                        </VideoThumbnail>
                                    </VideoThumbnailContainer>
                                    <VideoContainer>
                                        <PlayButton onClick={handlePlayVideo}>
                                            <Play style={{ width: 80, height: 80, color: ColorSAP.neutral[0] }} />
                                        </PlayButton>
                                        <div style={{ position: "absolute", top: isMobileTabletDisplay ? 40 : 64, right: isMobileTabletDisplay ? 48 : 120 }}>
                                            <LanguageSwitcher
                                                i18n={i18n}
                                                onChangeLanguageClick={onChangeLanguageClick}
                                                theme="dark"
                                            />
                                        </div>
                                    </VideoContainer>
                                </>
                            }
                            {isMobileDisplay && 
                                <div style={{ float: "inline-end", margin: "40px 24px 24px 24px" }}>
                                    <LanguageSwitcher
                                        i18n={i18n}
                                        onChangeLanguageClick={onChangeLanguageClick}
                                        theme="dark"
                                    />
                                </div>
                            }
                        </Grid>
                        <Grid item size={{ sm: 12, md: 6 }}>
                            <Grid container style={{ height: "100%" }}>
                                <Grid item size={12}>
                                    <LoginContainer>
                                        <Grid container>
                                            <Grid item size={12} style={{ marginBottom: isMobileDisplay ? 4 : 24 }}>
                                                {!isMobileTabletDisplay && 
                                                    <LogoDark
                                                        style={{
                                                            width: 158,
                                                            height: 64,
                                                            marginBottom: 56
                                                        }}
                                                    />
                                                }
                                                <Title
                                                    fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_BOLD}
                                                    size={isMobileDisplay ? 32 : 56}
                                                    color={ColorSAP.neutral[100]}
                                                >
                                                    {t("login.info.title")}
                                                </Title>

                                                <Description
                                                    fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_REGULAR}
                                                    size={14}
                                                    color={ColorSAP.neutral[100]}
                                                >
                                                    {t("login.info.desc")}
                                                </Description>

                                                <Description
                                                    fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_REGULAR}
                                                    size={14}
                                                    color={ColorSAP.neutral[100]}
                                                >
                                                    <span>
                                                        {t("login.info.desc2")}
                                                    </span>
                                                    <span
                                                        style={{ color: ColorSAP.primary[70], cursor: 'pointer' }}
                                                        onClick={handlePlayVideo}
                                                    >
                                                        {t("login.info.link")}
                                                    </span>
                                                </Description>
                                            </Grid>

                                            <Grid item size={12} style={{ maxWidth: 488, marginBottom: isMobileDisplay ? 32 : 48 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item size={12}>
                                                        <div tabIndex={1}>
                                                            <Form
                                                                placeholder={`${t('common.email')}`}
                                                                label={`${t('common.email')} *`}
                                                                onChange={handleChangeEmail}
                                                                value={email}
                                                                error={emailError || emailInvalid}
                                                                errorMessage={emailError ? t("error.email") : t("error.emailInvalid")}
                                                            />
                                                        </div>
                                                    </Grid>

                                                    <Grid item size={12}>
                                                        <div tabIndex={2}>
                                                            <Form 
                                                                placeholder={`${t('common.password')}`}
                                                                label={`${t('common.password')} *`}
                                                                onChange={handleChangePassword}
                                                                isPassword
                                                                value={password}
                                                                error={passwordError}
                                                                errorMessage={t("error.password")}
                                                            />
                                                        </div>
                                                    </Grid>
                                                    {errorMessage !== "" && (
                                                        <Grid item size={12}>
                                                            <Typography
                                                                fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_REGULAR}
                                                                size={12}
                                                                color={ColorSAP.danger[50]}
                                                            >
                                                                {errorMessage}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Grid>

                                            <Grid item size={12}>
                                                <div tabIndex={3}>
                                                    <Button
                                                        id="loginButton"
                                                        type="primary"
                                                        theme="dark"
                                                        size={isMobileTabletDisplay ? "small" : "medium"}
                                                        onClick={login}
                                                    >
                                                        {t("login.login")}
                                                    </Button>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </LoginContainer>
                                </Grid>
                                {!isMobileTabletDisplay && 
                                    <Grid item size={12} style={{ display: "flex", alignItems: "end" }}>
                                        <FooterContainer>
                                            <LinkRegister
                                                fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_LIGHT}
                                                size={14}
                                                color={ColorSAP.neutral[100]}
                                            >
                                                {t("login.dontHaveAcc")}
                                            </LinkRegister>
                                            <Link
                                                to={REGISTER_PATH} style={{ textDecoration: "none" }}
                                            >
                                                <LinkRegister
                                                    fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_MEDIUM}
                                                    size={14}
                                                    color={ColorSAP.primary[70]}
                                                    style={{ marginLeft: 10, cursor: "pointer" }}
                                                >
                                                {t("login.register")}
                                                </LinkRegister>
                                            </Link>
                                        </FooterContainer>
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {isMobileTabletDisplay && 
                    <Grid item size={12} style={{ display: "flex", alignItems: "end" }}>
                        <FooterContainer>
                            <LinkRegister
                                fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_LIGHT}
                                size={14}
                                color={ColorSAP.neutral[100]}
                            >
                                {t("login.dontHaveAcc")}
                            </LinkRegister>
                            <Link
                                to={REGISTER_PATH} style={{ textDecoration: "none" }}
                            >
                                <LinkRegister
                                    fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_MEDIUM}
                                    size={14}
                                    color={ColorSAP.primary[70]}
                                    style={{ marginLeft: 10, cursor: "pointer" }}
                                >
                                {t("login.register")}
                                </LinkRegister>
                            </Link>
                        </FooterContainer>
                    </Grid>
                }
            </Grid>
            <VideoDialog
                open={isVideoDialogOpen}
                onClose={handleCloseVideo}
            />
        </RootContainer>
    )
}

export default Login;